import React, { memo, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const TimesheetSummary = memo(({ summary, dateRange }) => {
  const getPeriodLabel = (range) => {
    if (!range || (!range.start && !range.end)) {
      return 'All Entries';
    }

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString + 'T00:00:00');
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      });
    };

    if (range.start === range.end) {
      return formatDate(range.start);
    }

    return `${formatDate(range.start)} - ${formatDate(range.end)}`;
  };

  const formatHours = (hours) => {
    return typeof hours === 'number' ? hours.toFixed(2) : '0.00';
  };

  const formatAmount = (amount) => {
    return typeof amount === 'number' ? amount.toFixed(2) : '0.00';
  };

  // Memoize summary cards calculation
  const summaryCards = useMemo(() => [
    {
      title: 'Total Hours',
      value: `${formatHours(summary.totalHours)}h`,
      icon: 'Clock',
      color: 'bg-blue-50',
      iconColor: 'text-blue-500',
      description: 'All logged hours'
    },
    {
      title: 'Approved',
      value: `${formatHours(summary.approvedHours)}h`,
      icon: 'CheckCircle',
      color: 'bg-green-50',
      iconColor: 'text-green-500',
      description: 'Approved by employer'
    },
    {
      title: 'Pending',
      value: `${formatHours(summary.pendingHours)}h`,
      icon: 'Clock',
      color: 'bg-yellow-50',
      iconColor: 'text-yellow-500',
      description: 'Awaiting approval'
    },
    {
      title: 'Earnings',
      value: `$${formatAmount(summary.approvedAmount)}`,
      icon: 'DollarSign',
      color: 'bg-purple-50',
      iconColor: 'text-purple-500',
      description: `$${formatAmount(summary.totalAmount)} total`
    }
  ], [summary]);

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Summary</h2>
        <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
          {getPeriodLabel(dateRange)}
        </span>
      </div>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card, index) => (
          <div key={index} className="text-center">
            <div className={`w-14 h-14 rounded-xl ${card.color} flex items-center justify-center mx-auto mb-3`}>
              <Icon name={card.icon} size={24} className={card.iconColor} />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {card.value}
            </div>
            <div className="text-xs font-medium text-gray-700 mb-1">
              {card.title}
            </div>
            <div className="text-xs text-gray-500">
              {card.description}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Memo comparison - only re-render if summary values changed
  return (
    prevProps.summary?.totalHours === nextProps.summary?.totalHours &&
    prevProps.summary?.approvedHours === nextProps.summary?.approvedHours &&
    prevProps.summary?.pendingHours === nextProps.summary?.pendingHours &&
    prevProps.summary?.approvedAmount === nextProps.summary?.approvedAmount &&
    prevProps.dateRange?.start === nextProps.dateRange?.start &&
    prevProps.dateRange?.end === nextProps.dateRange?.end
  );
});

TimesheetSummary.displayName = 'TimesheetSummary';

export default TimesheetSummary;
